import { memo } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { DayOrdersStatistics } from '@api/shipping-data/types/DayOrdersStatistics';
import { TileFilter } from '@api/shipping-data/types/TileFilter';
import { RedatedTypeEnum } from '@api/order-reporting/types/RedatedParams';
import { OrderPhaseEnum } from '@api/order-reporting/types/ReducedParams';
import { AppRolesEnum } from '@features/routing/appRolesEnum';
import { NavigateRoutesEnum } from '@features/routing/routesEnum';
import { useHasPermission } from '@hooks/has-permission/useHasPermission';
import { selectedDateWithoutTime } from '@store/global/selectors';
import { convertToOrdersStatisticModel } from '@pages/orders/statistics/convertors';
import { setOrdersFilter } from '@pages/orders/store/slice';
import { setFilterDate, setDateRange as setRedatedDates } from '@pages/reporting-pages/redated-orders/store/slice';
import { setOrderPhase, setDateRange as setReducedDates } from '@pages/reporting-pages/reduced-orders/store/slice';
import StatisticCard from 'components/statistic-card/StatisticCard';
import { DateProgressCard } from 'components/date-progress-card/DateProgressCard';
import { DateRange } from '@project-types/DateRange';



interface StatisticProps {
	statistics: DayOrdersStatistics | null | undefined,
}


export const Statistic = (props: StatisticProps) => {
	const { statistics } = props;

	const convertedModel = convertToOrdersStatisticModel(statistics);
	const deliveredPercent = statistics && statistics.totalDeliveredOrdersPct ? statistics.totalDeliveredOrdersPct : 0;

	const dateWithoutTime = useSelector(selectedDateWithoutTime);
	const navigate = useNavigate();
	const hasPermission = useHasPermission();
	const dispatch = useDispatch();
	const updateRedatedDates = (dateRange: DateRange) => dispatch(setRedatedDates(dateRange));
	const updateReducedDates = (dateRange: DateRange) => dispatch(setReducedDates(dateRange));
	const updateRedatedType = (value: RedatedTypeEnum) => dispatch(setFilterDate(value));
	const updateOrdersFilter = (filter: TileFilter) => dispatch(setOrdersFilter(filter));
	const updateOrdersOrderPhase = (orderPhase: string) => dispatch(setOrderPhase(orderPhase));


	if (!convertedModel) return (
		<div className='statistics-area'>
			<DateProgressCard deliveredPercent={deliveredPercent} />
		</div>
	);


	return (
		<div className='statistics-area'>

			<DateProgressCard
				deliveredPercent={deliveredPercent} />

			<StatisticCard
				data={convertedModel.totalOrders}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.totalOrders);
				}} />

			<StatisticCard
				data={convertedModel.outgoingRedated}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.redatedOrdersReport_Read)) return;
					updateRedatedDates({ from: dateWithoutTime, to: dateWithoutTime });
					updateRedatedType(RedatedTypeEnum.outgoing);
					navigate(NavigateRoutesEnum.reportingRedatedOrders);
				}} />

			<StatisticCard
				data={convertedModel.cancelledPlanning}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.cancelledBeforeDelivery);
				}} />

			<StatisticCard
				data={convertedModel.reducedPlanning}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.reducedOrdersReport_Read)) return;
					updateReducedDates({ from: dateWithoutTime, to: dateWithoutTime });
					updateOrdersOrderPhase(String(OrderPhaseEnum.planning));
					navigate(NavigateRoutesEnum.reportingReducedOrders);
				}} />

			<StatisticCard
				data={convertedModel.changedPlanning}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.allChangesBeforeDelivery);
				}} />

			<StatisticCard
				data={convertedModel.perfectlyDelivered}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.perfectlyDelivered);
				}} />

			<StatisticCard
				data={convertedModel.notDelivered}
				onPress={() => {
					if (!hasPermission(AppRolesEnum.orders_Read)) return;
					updateOrdersFilter(TileFilter.notDelivered);
				}} />
		</div>
	)
}

export default memo(Statistic);